import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Select } from './Select';

const SIZES = [10, 25, 50, 100];

/** Client-side paging of a list: the current page's rows and the props for <Pager>. */
export function usePaged<T>(items: T[], initialSize = 25) {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(initialSize);
  const pages = Math.max(1, Math.ceil(items.length / size));
  // A shorter list (filter, delete) keeps the pager on a page that exists.
  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);
  const rows = useMemo(() => items.slice((page - 1) * size, page * size), [items, page, size]);
  return {
    rows,
    pager: { page: Math.min(page, pages), size, total: items.length, onPage: setPage, onSize: (n: number) => { setSize(n); setPage(1); } },
  };
}

/** Page numbers to show: first, last and around the current one; 0 = a gap. */
function pageList(page: number, pages: number): number[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const set = new Set([1, pages, page - 1, page, page + 1].filter((p) => p >= 1 && p <= pages));
  if (page <= 3) [2, 3, 4].forEach((p) => set.add(p));
  if (page >= pages - 2) [pages - 1, pages - 2, pages - 3].forEach((p) => set.add(p));
  const sorted = [...set].sort((a, b) => a - b);
  const out: number[] = [];
  sorted.forEach((p, i) => {
    if (i && p - sorted[i - 1]! > 1) out.push(0);
    out.push(p);
  });
  return out;
}

/** PulsePanels pager: "1–25 of 131", rows per page, Previous · 1 2 3 … 14 · Next. Hidden for one short page. */
export function Pager({ page, size, total, onPage, onSize }: { page: number; size: number; total: number; onPage: (p: number) => void; onSize: (n: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / size));
  if (total <= SIZES[0]) return null;
  const start = total === 0 ? 0 : (page - 1) * size + 1;
  const end = Math.min(total, page * size);
  return (
    <div className="pager">
      <div className="pager-left">
        <span className="pager-range">
          {start}–{end} of {total}
        </span>
        <Select size="sm" aria-label="Rows per page" value={String(size)} onChange={(e) => onSize(Number(e.target.value))}>
          {SIZES.map((n) => (
            <option key={n} value={n}>
              {n} per page
            </option>
          ))}
        </Select>
      </div>
      <div className="pager-right">
        <button type="button" className="pager-btn" onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1}>
          <ChevronLeft size={15} /> Previous
        </button>
        {pageList(page, pages).map((p, i) =>
          p === 0 ? (
            <span key={`gap${i}`} className="pager-gap">…</span>
          ) : (
            <button key={p} type="button" className={`pager-num${p === page ? ' on' : ''}`} onClick={() => onPage(p)}>
              {p}
            </button>
          ),
        )}
        <button type="button" className="pager-btn" onClick={() => onPage(Math.min(pages, page + 1))} disabled={page >= pages}>
          Next <ChevronRight size={15} />
        </button>
      </div>
    </div>
  );
}
